import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';
import { inject, Injectable, Injector } from '@angular/core';
import { NEVER, Observable, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { AuthService } from '@auth0/auth0-angular';
import { environment } from '../../../environments/environment';
import { LocalStorageKeys, LocalStorageService } from './local-storage.service';
import { MaintenanceStore } from '../store/maintenance/maintenance.store';

@Injectable({
  providedIn: 'root',
})
export class HttpGatewayService {
  private readonly injector = inject(Injector);

  constructor(
    private http: HttpClient,
    private auth: AuthService,
  ) {}

  public get<T>(url: string, params?: HttpParams): Observable<T> {
    return this.createHeaders().pipe(
      switchMap((headers) =>
        this.http.get(this.resolveURL(url), {
          headers,
          params: params,
        }),
      ),
      map((serverResponse) => serverResponse as T),
      catchError(this.handleError.bind(this)),
    );
  }

  public getByResponseType<T>(
    url: string,
    responseType?: 'json' | undefined,
    params?: HttpParams,
  ): Observable<T> {
    return this.createHeaders().pipe(
      switchMap((headers) =>
        this.http.get(this.resolveURL(url), {
          responseType: responseType,
          headers,
          params: params,
        }),
      ),
      map((serverResponse) => serverResponse as T),
      catchError(this.handleError.bind(this)),
    );
  }

  public delete<T>(
    url: string,
    params?: HttpParams,
    body?: any,
  ): Observable<T> {
    return this.createHeaders().pipe(
      switchMap((headers) =>
        this.http.delete(this.resolveURL(url), {
          headers,
          params: params,
          body,
        }),
      ),
      map((serverResponse) => serverResponse as T),
      catchError(this.handleError.bind(this)),
    );
  }

  public post<T>(url: string, body: any): Observable<T> {
    return this.createHeaders().pipe(
      switchMap((headers) =>
        this.http.post(this.resolveURL(url), body, {
          headers,
        }),
      ),
      map((serverResponse) => serverResponse as T),
      catchError(this.handleError.bind(this)),
    );
  }

  public put<T>(url: string, body: any): Observable<T> {
    return this.createHeaders().pipe(
      switchMap((headers) =>
        this.http.put(this.resolveURL(url), body, {
          headers,
        }),
      ),
      map((serverResponse) => serverResponse as T),
      catchError(this.handleError.bind(this)),
    );
  }

  private resolveURL(url: string): string {
    return environment.api.serverUrl + url;
  }

  private createHeaders(): Observable<HttpHeaders> {
    const headers: {
      [name: string]: string | number | (string | number)[];
    } = {
      'Content-Type': 'application/json',
    };
    const impersonation = LocalStorageService.get(
      LocalStorageKeys.IMPERSONATION_TOKEN,
    );
    if (impersonation) {
      headers['Impersonation'] = impersonation;
    }

    const skipMaintenance = LocalStorageService.get(
      LocalStorageKeys.SKIP_MAINTENANCE,
    );
    if (skipMaintenance) {
      headers['skip-maintenance'] = skipMaintenance;
    }

    return this.auth.getAccessTokenSilently().pipe(
      map((token) => {
        headers['Authorization'] = 'Bearer ' + token;
        return new HttpHeaders(headers);
      }),
    );
  }

  private handleUnauthorized(): void {}

  // Resolved lazily to avoid a construction-time DI cycle
  // (HttpGatewayService -> MaintenanceStore -> MaintenanceApi -> HttpGatewayService).
  private handleMaintenanceMode(): void {
    this.injector.get(MaintenanceStore).setUnderMaintenance(true);
  }

  private handleError<T>(errorResponse: HttpErrorResponse): Observable<never> {
    if (errorResponse.status === 401) {
      this.handleUnauthorized();
      return NEVER;
    } else if (errorResponse.status === 400) {
      return throwError(() => errorResponse.error);
    } else if (errorResponse.status === 500) {
      return throwError(() => errorResponse.error);
    } else if (errorResponse.status === 503) {
      this.handleMaintenanceMode();
      return NEVER;
    } else {
      return throwError(() => errorResponse.error);
    }
  }
}

export interface ApiError {
  message: string;
  error: string;
  statusCode: number;
  correlationId?: string;
}

export const isApiError = (error: unknown): error is ApiError => {
  return (
    error !== undefined &&
    error !== null &&
    (error as ApiError).message !== undefined &&
    (error as ApiError).error !== undefined &&
    (error as ApiError).statusCode !== undefined
  );
};
